import { useState, FormEvent } from 'react';

interface Props {
  onClose: () => void;
}

const interestOptions = [
  { value: 'listing', label: 'Mama Approved Listing', icon: 'ri-medal-line' },
  { value: 'featured', label: 'Featured Event Placement', icon: 'ri-pushpin-2-line' },
  { value: 'partner', label: 'Mama Preferred Partner ($97/mo)', icon: 'ri-vip-crown-line' },
];

export default function PartnerApplyForm({ onClose }: Props) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedInterest, setSelectedInterest] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedInterest) return;
    setSubmitting(true);
    const form = e.currentTarget;
    const formData = new FormData(form);
    formData.set('interest', selectedInterest);
    const body = new URLSearchParams();
    formData.forEach((value, key) => body.append(key, value.toString()));
    try {
      await fetch('https://readdy.ai/api/form/d6t0nho37h4e5cfhq0k0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 overflow-y-auto">
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden my-auto">

        {/* Header */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-7 py-6">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-white/10 rounded-full hover:bg-white/20 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-white text-base" />
          </button>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 flex items-center justify-center bg-rose-500/20 rounded-full">
              <i className="ri-handshake-fill text-rose-400 text-xs" />
            </div>
            <span className="text-[11px] font-bold text-rose-400 uppercase tracking-widest">
              The Mommy Mingle
            </span>
          </div>
          <h2 className="text-xl font-extrabold text-white leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>
            Apply to Partner With<br />The Mommy Mingle
          </h2>
          <p className="text-white/50 text-xs mt-2 leading-relaxed max-w-sm">
            We partner with select local businesses to bring high-quality experiences, services, and resources to our moms. If that&apos;s you, apply below.
          </p>
        </div>

        {submitted ? (
          <div className="px-7 py-14 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-rose-100 rounded-full mx-auto mb-5">
              <i className="ri-checkbox-circle-fill text-rose-500 text-3xl" />
            </div>
            <h3 className="text-xl font-extrabold text-gray-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Application Received!
            </h3>
            <p className="text-gray-500 text-sm leading-relaxed max-w-xs mx-auto">
              We&apos;ll review your application and be in touch within 2–3 business days.
            </p>
            <button
              onClick={onClose}
              className="mt-7 px-8 py-3 bg-rose-500 text-white font-bold rounded-full hover:bg-rose-600 transition-colors cursor-pointer text-sm whitespace-nowrap"
            >
              Done
            </button>
          </div>
        ) : (
          <form
            data-readdy-form
            id="partner-application-v2"
            onSubmit={handleSubmit}
            className="px-7 py-6 space-y-4"
          >
            <div className="grid grid-cols-2 gap-4">
              {/* Business Name */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">
                  Business Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  name="business_name"
                  required
                  placeholder="Your business name"
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-rose-300 transition-all"
                />
              </div>

              {/* Contact Name */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">
                  Contact Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  name="contact_name"
                  required
                  placeholder="First and last name"
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-rose-300 transition-all"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Email */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">
                  Email <span className="text-rose-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="your@email.com"
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-rose-300 transition-all"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">
                  Phone <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="(555) 000-0000"
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-rose-300 transition-all"
                />
              </div>
            </div>

            {/* Type of Business */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">
                Type of Business <span className="text-rose-500">*</span>
              </label>
              <select
                name="business_type"
                required
                defaultValue=""
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-rose-300 cursor-pointer bg-white transition-all"
              >
                <option value="" disabled>Select a category</option>
                <option>Photography &amp; Portraits</option>
                <option>Fitness &amp; Wellness</option>
                <option>Party &amp; Events</option>
                <option>Hair &amp; Beauty</option>
                <option>Kids Activities &amp; Classes</option>
                <option>Tutors &amp; Education</option>
                <option>Health &amp; Medical</option>
                <option>Food &amp; Dining</option>
                <option>Mom-Owned Business</option>
                <option>Home Services</option>
                <option>Other</option>
              </select>
            </div>

            {/* Website / Social */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">
                Website or Social Link
              </label>
              <input
                type="url"
                name="website"
                placeholder="https://yoursite.com or @handle"
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-rose-300 transition-all"
              />
            </div>

            {/* What are you looking for */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-2">
                What are you looking for? <span className="text-rose-500">*</span>
              </label>
              <div className="space-y-2">
                {interestOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setSelectedInterest(option.value)}
                    className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl border text-sm font-medium transition-all cursor-pointer text-left ${
                      selectedInterest === option.value
                        ? 'border-rose-400 bg-rose-50 text-rose-700'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-rose-200 hover:bg-rose-50/40'
                    }`}
                  >
                    <div className={`w-5 h-5 flex items-center justify-center rounded-full border-2 shrink-0 transition-all ${
                      selectedInterest === option.value ? 'border-rose-500 bg-rose-500' : 'border-gray-300'
                    }`}>
                      {selectedInterest === option.value && (
                        <div className="w-2 h-2 rounded-full bg-white" />
                      )}
                    </div>
                    <div className="w-5 h-5 flex items-center justify-center shrink-0">
                      <i className={`${option.icon} text-sm ${selectedInterest === option.value ? 'text-rose-500' : 'text-gray-400'}`} />
                    </div>
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting || !selectedInterest}
              className="w-full py-3.5 bg-rose-500 text-white font-bold rounded-xl hover:bg-rose-600 transition-colors whitespace-nowrap cursor-pointer text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <><i className="ri-loader-4-line animate-spin text-base" /> Submitting...</>
              ) : (
                <><i className="ri-send-plane-fill text-base" /> Submit Application</>
              )}
            </button>

            <p className="text-center text-[11px] text-gray-400">
              We review every application · Response within 2–3 business days
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
